import { ArrowLeftOutlined, PaperClipOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Empty,
  Flex,
  Row,
  Spin,
  Tag,
  Typography
} from 'antd'
import { DescriptionsItemType } from 'antd/es/descriptions'
import { useState } from 'react'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom'

import {
  useCounterpartServiceGetInternalInstitutionEndpoint,
  useInvestigationsServiceGetInvestigationDemandEndpoint,
  useInvestigationsServiceGetInvestigationTestsEndpoint,
  useInvestigationsServiceGetInvestigationTestsEndpointKey,
  useInvestigationsServiceGetListSpecimenInvestigationsEndpoint,
  useInvestigationsServiceGetListSpecimensEndpointKey,
  useInvestigationsServiceGetSpecimenEndpoint
} from 'src/api/queries'
import {
  InstitutionUserDto,
  InvestigationInstitutionDto,
  InvestigationTestResultDto,
  InvestigationTestsListItemDto,
  InvestigationTestStatus,
  SpecimenInvestigationItemDto
} from 'src/api/requests'
import { SearchBar, Select, TbTag, Text, TestStatusTag } from 'src/components'
import { ResultActions } from 'src/features/persons/components'
import { isExternalResult } from 'src/features/persons/utils'
import { onDownloadFile } from 'src/hooks'
import { formatUtcDateTime } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

const PAGE_SIZE = 50

type Translation = { ro?: string | null; ru?: string | null } | null | undefined

// A Descriptions item paired with the raw value it renders, so presence is
// decided from the data rather than from the rendered node.
type ValuedItem = DescriptionsItemType & { value: unknown }

const hasValue = (value: unknown) =>
  value !== null && value !== undefined && value !== ''

// Drops items whose underlying value is missing instead of rendering '-'.
const compactItems = (items: ValuedItem[]): DescriptionsItemType[] =>
  items
    .filter((item) => hasValue(item.value))
    .map(({ value: _value, ...item }) => item)

// Resolves an institution name. The list/demand DTOs sometimes return only the
// institution id with a null name, so we fetch it by id in that case.
const InstitutionName = ({
  institution
}: {
  institution?: InvestigationInstitutionDto | null
}) => {
  const { data } = useCounterpartServiceGetInternalInstitutionEndpoint(
    { id: String(institution?.id) },
    [String(institution?.id)],
    { enabled: !!institution?.id && !institution?.name }
  )

  return <>{institution?.name || data?.name || '-'}</>
}

export const PersonInvestigationResultsPage = () => {
  const { t, currentLang } = useLocales()
  const { personId, id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const specimenId = searchParams.get('specimenId') ?? undefined
  const from = searchParams.get('from')

  const isPatientContext = location.pathname.includes('/patients/')

  const backPath = isPatientContext
    ? `${paths.patients.details(String(personId ?? id))}${paths.patients.investigations.root()}`
    : `${paths.persons.details(String(personId ?? id))}${
        from === 'investigations'
          ? paths.persons.investigations.root()
          : paths.persons.screening.root()
      }`

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<InvestigationTestStatus>()

  const queryParams = {
    pageNumber: 1,
    pageSize: PAGE_SIZE,
    personId: String(personId ?? id),
    ...(specimenId && { specimenIds: [specimenId] }),
    ...(!!search && { searchTerm: search }),
    ...(status && { status })
  }

  const { data, isLoading } =
    useInvestigationsServiceGetInvestigationTestsEndpoint(
      queryParams,
      [JSON.stringify(queryParams)],
      { enabled: !!(personId ?? id) }
    )

  const queryClient = useQueryClient()

  const invalidateResults = () => {
    queryClient.invalidateQueries({
      queryKey: [useInvestigationsServiceGetInvestigationTestsEndpointKey]
    })
    queryClient.invalidateQueries({
      queryKey: [useInvestigationsServiceGetListSpecimensEndpointKey]
    })
  }

  const { data: specimen } = useInvestigationsServiceGetSpecimenEndpoint(
    { id: String(specimenId) },
    [String(specimenId)],
    { enabled: !!specimenId }
  )

  // The tests list doesn't carry the investigation type, so we fetch the
  // specimen's investigations (which do) and map them by investigation id.
  const { data: specimenInvestigations } =
    useInvestigationsServiceGetListSpecimenInvestigationsEndpoint(
      { institutionIds: [], specimenIds: [String(specimenId)] },
      [String(specimenId)],
      { enabled: !!specimenId }
    )

  const typeByInvestigationId = new Map(
    (specimenInvestigations?.data ?? []).map((investigation) => [
      investigation.id,
      investigation.type
    ])
  )

  const hasTests = !!data?.data?.length
  const hasInvestigations = !!specimenInvestigations?.data?.length

  const demandId = specimen?.demand?.id

  // A screening result reads the same wherever it is opened (Screening or Investigații tab).
  const isScreening = !!specimen?.demand?.isScreening || from === 'screening'

  // The specimen's demand returns reasons as ids with null names; the enriched
  // demand endpoint resolves them (and the institution) to displayable values.
  const { data: demand } =
    useInvestigationsServiceGetInvestigationDemandEndpoint(
      { id: String(demandId) },
      [String(demandId)],
      { enabled: !!demandId }
    )

  const nameOf = (translation: Translation) =>
    translation?.[currentLang.value as 'ro' | 'ru'] || undefined

  const userName = (user?: InstitutionUserDto | null) => {
    const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ')

    return name || undefined
  }

  const primaryReason = nameOf(demand?.investigationReason?.translation)
  const secondaryReasons = (demand?.secondaryInvestigationReasons ?? [])
    .map((reason) => nameOf(reason?.translation))
    .filter(Boolean)

  const summaryItems: DescriptionsItemType[] = [
    ...(primaryReason
      ? [
          {
            key: 'primary_reason',
            label: t('cases.primary_request_reason'),
            children: primaryReason
          }
        ]
      : []),
    ...(secondaryReasons.length
      ? [
          {
            key: 'secondary_reasons',
            label: t('cases.secondary_request_reason'),
            children: secondaryReasons.join(', ')
          }
        ]
      : []),
    ...(demand?.institution
      ? [
          {
            key: 'institution',
            label: t('laboratory_classifiers.selected_institution'),
            children: <InstitutionName institution={demand.institution} />
          }
        ]
      : []),
    ...(demand?.referralDoctorName || demand?.referralDoctorUser
      ? [
          {
            key: 'referral_doctor',
            label: t('investigations.referral_doctor'),
            children:
              demand.referralDoctorName || userName(demand.referralDoctorUser)
          }
        ]
      : [])
  ]

  const renderResult = (
    result: InvestigationTestResultDto,
    index: number,
    test: InvestigationTestsListItemDto
  ) => {
    const isExternal = isExternalResult(result)

    const externalItems: ValuedItem[] = isExternal
      ? [
          {
            key: 'sample_collected_at',
            label: t('persons.external_result.sample_collected_at'),
            value: result.sampleCollectedAt,
            children: formatUtcDateTime(result.sampleCollectedAt)
          },
          {
            key: 'source_laboratory',
            label: t('persons.external_result.source_laboratory'),
            value: result.sourceLaboratoryName || result.sourceLaboratoryId,
            children: result.sourceLaboratoryName || (
              <InstitutionName
                institution={{ id: result.sourceLaboratoryId ?? undefined }}
              />
            )
          }
        ]
      : []

    const testTypeName = nameOf(result.testType?.translation)
    const resultOptionName = nameOf(result.resultOption?.translation)
    const resultValueOptionName = nameOf(result.resultValueOption?.translation)

    const primaryItems: ValuedItem[] = [
      {
        key: 'test_type',
        label: t('tests_results.type'),
        value: testTypeName,
        children: testTypeName
      },
      {
        key: 'result_option',
        label: t('specimens.result_option'),
        value: resultOptionName,
        children: resultOptionName
      },
      {
        key: 'result_value',
        label: t('investigations.result_value'),
        value: result.resultValue,
        children: result.resultValue
      },
      {
        key: 'result_value_option',
        label: t('tests_results.result_value_option'),
        value: resultValueOptionName,
        children: resultValueOptionName
      },
      {
        key: 'result_date',
        label: t('tests_results.result_date'),
        value: result.resultDate,
        children: formatUtcDateTime(result.resultDate)
      }
    ]

    // Device reports the cartridge expiration raw as YYYYMMDD; format it to
    // DD.MM.YYYY when it matches that shape, otherwise show the raw value.
    const rawExpiration = result.cartridgeExpirationDate
    const cartridgeExpiration = rawExpiration
      ? /^\d{8}$/.test(rawExpiration)
        ? `${rawExpiration.slice(6, 8)}.${rawExpiration.slice(
            4,
            6
          )}.${rawExpiration.slice(0, 4)}`
        : rawExpiration
      : null

    const deviceItems: ValuedItem[] = [
      {
        key: 'device_test_type',
        label: t('tests_results.device_test_type'),
        value: result.laboratoryTestType,
        children: result.laboratoryTestType
      },
      {
        key: 'device_result',
        label: t('tests_results.device_result'),
        value: result.laboratoryResultStatus,
        children: result.laboratoryResultStatus
      },
      {
        key: 'cartridge_serial_number',
        label: t('tests_results.cartridge_serial_number'),
        value: result.cartridgeSerialNumber,
        children: result.cartridgeSerialNumber
      },
      {
        key: 'reagent_lot_id',
        label: t('tests_results.reagent_lot_id'),
        value: result.reagentLotId,
        children: result.reagentLotId
      },
      {
        key: 'cartridge_expiration_date',
        label: t('tests_results.cartridge_expiration_date'),
        value: cartridgeExpiration,
        children: cartridgeExpiration
      }
    ]

    const validationInstitution = result.validatedByUser?.institution
    const createdInstitution = result.createdByUser?.institution
    const createdByName = userName(result.createdByUser)
    const validatedByName = userName(result.validatedByUser)
    const testedByName = userName(result.testedByUser)
    const receivedByName = userName(specimen?.receivedByUser)

    // A screening result is not validated by a laboratory, so both institutions are hidden there
    const institutionItems: ValuedItem[] = isScreening
      ? []
      : [
          {
            key: 'validation_institution',
            label: t('tests_results.validation_institution'),
            value: validationInstitution?.id || validationInstitution?.name,
            children: <InstitutionName institution={validationInstitution} />
          },
          {
            key: 'created_institution',
            label: t('tests_results.created_institution'),
            value: createdInstitution?.id || createdInstitution?.name,
            children: <InstitutionName institution={createdInstitution} />
          }
        ]

    const authorItems: ValuedItem[] = [
      ...institutionItems,
      {
        key: 'created_by',
        label: t('tests_results.created_by'),
        value: createdByName,
        children: createdByName
      },
      // A screening result is entered by the doctor who did the initial
      // testing; the laboratory flow shows the same user as the validator.
      isScreening
        ? {
            key: 'tested_by',
            label: t('tests_results.tested_by_initial'),
            value: testedByName,
            children: testedByName
          }
        : {
            key: 'validated_by',
            label: t('tests_results.validated_by'),
            value: validatedByName,
            children: validatedByName
          },
      {
        key: 'received_by',
        label: t('tests_results.received_by'),
        value: receivedByName,
        children: receivedByName
      }
    ]

    // Each non-empty block becomes a column; spans are rebalanced so the
    // remaining columns fill the row.
    const resultColumns = [
      {
        key: 'primary',
        items: compactItems([...primaryItems, ...externalItems])
      },
      { key: 'device', items: compactItems(deviceItems) },
      { key: 'author', items: compactItems(authorItems) }
    ].filter((column) => column.items.length)

    const columnSpan = resultColumns.length ? 24 / resultColumns.length : 24

    const referralDoctorUserName = userName(test.referralDoctorUser)

    return (
      <div
        key={index}
        style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12 }}
      >
        <Flex
          justify="space-between"
          align="center"
          gap={8}
          wrap="wrap"
          style={{ marginBottom: 12 }}
        >
          {isExternal ? (
            <Tag color="orange">{t('persons.external_result.badge')}</Tag>
          ) : (
            <span />
          )}

          <ResultActions
            result={result}
            isApproved={test.isApproved}
            specimenId={specimenId}
            referralDoctorUserId={test.referralDoctorUser?.id}
            referralDoctorName={test.referralDoctorName}
            referralDoctorUserName={referralDoctorUserName}
            typeId={typeByInvestigationId.get(test.investigationId)?.id}
            onChanged={invalidateResults}
          />
        </Flex>

        {!!resultColumns.length && (
          <Row gutter={[16, 16]}>
            {resultColumns.map((column) => (
              <Col key={column.key} xs={24} md={columnSpan}>
                <Descriptions size="small" column={1} items={column.items} />
              </Col>
            ))}
          </Row>
        )}

        {!!result.mediaItems?.length && (
          <Flex vertical gap={2} style={{ marginTop: 12 }}>
            <Text type="secondary" size="sm">
              {t('stock_applications.uploaded_documents')}
            </Text>
            {result.mediaItems.map((media) => (
              <Typography.Link
                key={media.id}
                className="fit-content"
                onClick={() => onDownloadFile(t, media.id, media.name)}
              >
                <Flex gap={4} align="center">
                  <PaperClipOutlined />
                  {media.name}
                </Flex>
              </Typography.Link>
            ))}
          </Flex>
        )}
      </div>
    )
  }

  const renderTest = (test: InvestigationTestsListItemDto) => {
    const investigationTypeName = nameOf(
      typeByInvestigationId.get(test.investigationId)?.translation
    )
    const categoryName = nameOf(test.category?.translation)
    const specimenTypeName = nameOf(test.specimenType?.translation)

    const testItems = compactItems([
      {
        key: 'code',
        label: t('investigations.test_number'),
        value: test.code,
        children: test.code
      },
      {
        key: 'investigation_type',
        label: t('laboratory_classifiers.investigation_type'),
        value: investigationTypeName,
        children: investigationTypeName
      },
      {
        key: 'category',
        label: t('classifiers.category'),
        value: categoryName,
        children: categoryName
      },
      {
        key: 'specimen_type',
        label: t('consultations.specimen_type'),
        value: specimenTypeName,
        children: specimenTypeName
      },
      {
        key: 'testing_at',
        label: t('investigations.performed_date'),
        value: test.testingAt,
        children: formatUtcDateTime(test.testingAt)
      },
      {
        key: 'created_at',
        label: t('investigations.created_date'),
        value: test.createdAt,
        children: formatUtcDateTime(test.createdAt)
      },
      {
        key: 'rejected_at',
        label: t('investigations.reject'),
        value: test.rejectedAt,
        children: formatUtcDateTime(test.rejectedAt)
      }
    ])

    return (
      <Card
        key={test.id}
        size="small"
        title={
          <Flex gap={8} align="center" wrap="wrap">
            <Text weight={600}>{test.code ?? '-'}</Text>
            <TbTag isTb={test.isTb} />
            <TestStatusTag status={test.status} />
          </Flex>
        }
      >
        {!!testItems.length && (
          <Descriptions
            size="small"
            column={{ xs: 1, sm: 2, xl: 3 }}
            items={testItems}
          />
        )}

        <Divider orientation="left" style={{ margin: '12px 0' }}>
          {t('tests_results.result')}
        </Divider>

        {test.results?.length ? (
          <Flex vertical gap={12}>
            {test.results.map((result, index) =>
              renderResult(result, index, test)
            )}
          </Flex>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('tests_results.no_results')}
          />
        )}
      </Card>
    )
  }

  // Shown when the specimen has no tests/results yet: the investigations were
  // requested but not processed, so display what was requested for context.
  const renderInvestigation = (investigation: SpecimenInvestigationItemDto) => {
    const investigationTypeName = nameOf(investigation.type?.translation)
    const categoryName = nameOf(investigation.category?.translation)

    const investigationItems = compactItems([
      {
        key: 'investigation_type',
        label: t('laboratory_classifiers.investigation_type'),
        value: investigationTypeName,
        children: investigationTypeName
      },
      {
        key: 'category',
        label: t('classifiers.category'),
        value: categoryName,
        children: categoryName
      },
      {
        key: 'created_at',
        label: t('investigations.created_date'),
        value: investigation.createdAt,
        children: formatUtcDateTime(investigation.createdAt)
      }
    ])

    return (
      <Card
        key={investigation.id}
        size="small"
        title={
          <Flex gap={8} align="center" wrap="wrap">
            <Text weight={600}>{investigation.code ?? '-'}</Text>
            <TbTag isTb={investigation.isTb} />
          </Flex>
        }
      >
        {!!investigationItems.length && (
          <Descriptions
            size="small"
            column={{ xs: 1, sm: 2, xl: 3 }}
            items={investigationItems}
          />
        )}

        <Divider orientation="left" style={{ margin: '12px 0' }}>
          {t('tests_results.result')}
        </Divider>

        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('tests_results.no_results')}
        />
      </Card>
    )
  }

  return (
    <Flex vertical gap={16}>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(backPath)}
        style={{ alignSelf: 'flex-start', paddingLeft: 0 }}
      >
        {t('common.back')}
      </Button>

      <Flex justify="space-between" align="center" gap={8} wrap="wrap">
        <Text weight={600}>{t('persons.person_investigation-results')}</Text>

        <Flex gap={8}>
          <SearchBar
            maxWidth={300}
            onSearch={setSearch}
            placeholder={t('investigations.search_placeholder')}
          />

          <Select
            options={Object.values(InvestigationTestStatus).map((value) => ({
              label: t(`tests_results.statuses.${value}`),
              value
            }))}
            onChange={setStatus}
            onClear={() => setStatus(undefined)}
            allowClear
            placeholder={t('form.status')}
          />
        </Flex>
      </Flex>

      {!!summaryItems.length && (
        <Card size="small">
          <Descriptions size="small" column={1} items={summaryItems} />
        </Card>
      )}

      <Divider style={{ margin: 0 }} />

      <Spin spinning={isLoading}>
        {hasTests && (
          <Flex vertical gap={16}>
            {data?.data?.map(renderTest)}
          </Flex>
        )}

        {!hasTests && hasInvestigations && (
          <Flex vertical gap={16}>
            {specimenInvestigations?.data?.map(renderInvestigation)}
          </Flex>
        )}

        {!hasTests && !hasInvestigations && !isLoading && (
          <Empty description={t('tests_results.no_results')} />
        )}
      </Spin>
    </Flex>
  )
}
